// @ts-nocheck
import { useEffect, useRef, useState } from "react";
import { SubSection, FieldLabel, inputClass } from "./SectionWrapper";
import { Switch } from "@/components/ui/switch";
import { EventData } from "@/types/events";


interface Props {
  event: EventData;
  updateEvent: (updates: Partial<EventData>) => void;
}

/**
 * Text input for a comma-separated email list stored as string[].
 *
 * The list must be edited as free text: deriving `value` from
 * `array.join(", ")` on every render swallows keystrokes (typing a comma or a
 * trailing space is parsed away and immediately overwritten). So we keep the
 * raw text in local state and only re-seed it when the incoming array actually
 * changes to something different from what we have typed.
 */
function EmailListInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[] | null | undefined;
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const incoming = (value ?? []).join(", ");
  const [text, setText] = useState(incoming);
  const lastIncoming = useRef(incoming);

  useEffect(() => {
    if (incoming !== lastIncoming.current) {
      lastIncoming.current = incoming;
      setText(incoming);
    }
  }, [incoming]);

  const handleChange = (raw: string) => {
    setText(raw);
    const parsed = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    lastIncoming.current = parsed.join(", ");
    onChange(parsed);
  };

  return (
    <input
      type="text"
      value={text}
      onChange={(e) => handleChange(e.target.value)}
      placeholder={placeholder}
      className={inputClass}
    />
  );
}

/** A single alert row: on/off switch plus the addresses it sends to. */
function AlertRow({
  title,
  hint,
  enabled,
  onEnabledChange,
  children,
}: {
  title: string;
  hint: string;
  enabled: boolean;
  onEnabledChange: (next: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <FieldLabel>{title}</FieldLabel>
          <p className="text-xs text-muted-foreground -mt-1">{hint}</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
          <Switch checked={enabled} onCheckedChange={onEnabledChange} />
          {enabled ? "On" : "Off"}
        </label>
      </div>
      <div className={enabled ? "mt-3" : "mt-3 opacity-50"}>{children}</div>
    </div>
  );
}

export function NotificationsSection({ event, updateEvent }: Props) {
  const placeholder = "email1@example.com, email2@example.com";
  const on = (v: unknown) => v !== false;

  return (
    <div className="space-y-6">
      <SubSection title="Organiser Alerts">
        <p className="text-xs text-muted-foreground">
          These emails go to your staff, not to registrants. Turn an alert off to stop
          the sending without losing the addresses.
        </p>

        <AlertRow
          title="New registration alerts"
          hint="Team, division, coach, amount due and payment method on every registration."
          enabled={on(event.alert_registration_enabled)}
          onEnabledChange={(alert_registration_enabled) => updateEvent({ alert_registration_enabled })}
        >
          <EmailListInput
            value={event.registration_alert_emails}
            onChange={(registration_alert_emails) => updateEvent({ registration_alert_emails })}
            placeholder={placeholder}
          />
        </AlertRow>

        <AlertRow
          title="Payment alerts"
          hint="Every payment recorded, by card or by hand in the sheet."
          enabled={on(event.alert_payment_enabled)}
          onEnabledChange={(alert_payment_enabled) => updateEvent({ alert_payment_enabled })}
        >
          <EmailListInput
            value={event.payment_notification_emails}
            onChange={(payment_notification_emails) => updateEvent({ payment_notification_emails })}
            placeholder={placeholder}
          />
        </AlertRow>

        <AlertRow
          title="Roster submission alerts"
          hint="Team, division and player count when a roster is submitted."
          enabled={on(event.alert_roster_enabled)}
          onEnabledChange={(alert_roster_enabled) => updateEvent({ alert_roster_enabled })}
        >
          <EmailListInput
            value={event.roster_upload_emails}
            onChange={(roster_upload_emails) => updateEvent({ roster_upload_emails })}
            placeholder={placeholder}
          />
        </AlertRow>

        <AlertRow
          title="College coach registration alerts"
          hint="When a college coach registers to attend."
          enabled={on(event.alert_coach_registration_enabled)}
          onEnabledChange={(alert_coach_registration_enabled) =>
            updateEvent({ alert_coach_registration_enabled })
          }
        >
          <input
            type="text"
            value={event.coach_notification_email ?? ""}
            onChange={(e) => updateEvent({ coach_notification_email: e.target.value })}
            placeholder={placeholder}
            className={inputClass}
          />
        </AlertRow>

        <AlertRow
          title="Media credential request alerts"
          hint="Requests that need approving before credentials are issued."
          enabled={on(event.alert_media_request_enabled)}
          onEnabledChange={(alert_media_request_enabled) => updateEvent({ alert_media_request_enabled })}
        >
          <input
            type="text"
            value={event.media_credentials_notification_email ?? ""}
            onChange={(e) => updateEvent({ media_credentials_notification_email: e.target.value })}
            placeholder={placeholder}
            className={inputClass}
          />
        </AlertRow>
      </SubSection>
    </div>
  );
}

